import { useEffect, useState, useCallback, useRef } from 'react';
import { useJwt } from '../../../Hooks/useJwt';
import { useChatToken } from '../../../Hooks/useChatToken';
import {
   addReceiveDraftListener,
   addReceiveMessageListener,
   ensureConnected,
   isConnectionUp,
   removeReceiveDraftListener,
   removeReceiveMessageListener,
   startConnection,
   stopConnection,
   subscribeConnectionEvents
} from '../../../SignalRService';
import { Message } from '../models/message';
import { ConciergeMessageDraftResponse, ConciergeMessageResponse } from '../models/conciergeContracts';
import { showErrorBanner } from '../../../Components/ErrorBanner';
import { isTokenExpired, isUnauthorizedError } from '../../../ApiClientArchitecture/session';
import { ChatService } from '../services/chatService';
import { upsertMessage } from '../services/reconcile';
import { applyDraft, DraftsByTurn, markDraftedTurnSent } from '../services/drafts';

interface UseSignalRConnectionProps {
   setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
   /** Answer drafts by turn, beside the message list (`useMessageHandling` decides which are shown). */
   setDrafts: React.Dispatch<React.SetStateAction<DraftsByTurn>>;
   /** Refetches `GET /api/chat` and reconciles it; resolves to whether it succeeded. */
   recoverHistory: () => Promise<boolean>;
}

export const useSignalRConnection = ({ setMessages, setDrafts, recoverHistory }: UseSignalRConnectionProps) => {
   const [isConnecting, setIsConnecting] = useState(false);
   const [isConnected, setIsConnected] = useState(false);
   const token = useChatToken();
   const { decodeJwt } = useJwt();
   /** Set when messages may have been missed (reconnecting / closed / failed start). */
   const missedMessagesRef = useRef(false);

   const handleReceiveMessage = useCallback(
      (response: ConciergeMessageResponse) => {
         const message = ChatService.mapSignalRMessageToMessage(response);
         setMessages((prev) => upsertMessage(prev, message));
      },
      [setMessages]
   );

   // Each draft carries the whole answer so far: it replaces the turn's previous draft, never adds to it. It also proves
   // the server accepted the guest's message, which shows as sent in the same render (the POST waits for the whole turn).
   const handleReceiveDraft = useCallback(
      (response: ConciergeMessageDraftResponse) => {
         const draft = ChatService.mapSignalRDraftToDraft(response);
         if (!draft) return;
         setDrafts((prev) => applyDraft(prev, draft));
         const { clientTurnId } = draft;
         if (clientTurnId) setMessages((prev) => markDraftedTurnSent(prev, clientTurnId));
      },
      [setDrafts, setMessages]
   );

   const recover = useCallback(() => {
      recoverHistory().then((ok) => {
         if (ok) missedMessagesRef.current = false;
      });
   }, [recoverHistory]);

   useEffect(() => {
      let isMounted = true;
      const decodedToken = decodeJwt(token);

      // No connection attempt with an expired token: the chat page sends the guest to sign-in instead.
      if (!token || !decodedToken?.userId || isTokenExpired(decodedToken.exp)) {
         setIsConnected(false);
         setIsConnecting(false);
         return;
      }

      addReceiveMessageListener<ConciergeMessageResponse>(handleReceiveMessage);
      addReceiveDraftListener<ConciergeMessageDraftResponse>(handleReceiveDraft);

      const unsubscribe = subscribeConnectionEvents((event) => {
         if (!isMounted) return;
         switch (event) {
            case 'connected':
            case 'reconnected':
               setIsConnected(true);
               setIsConnecting(false);
               // Anything broadcast while we were not listening (including the
               // gap between the initial history load and this connect) is
               // recovered from history; reconciliation makes this idempotent.
               recover();
               break;
            case 'reconnecting':
               missedMessagesRef.current = true;
               setIsConnected(false);
               setIsConnecting(true);
               break;
            case 'disconnected':
               missedMessagesRef.current = true;
               setIsConnected(false);
               setIsConnecting(false);
               break;
         }
      });

      setIsConnecting(true);
      startConnection(decodedToken.tenantId, decodedToken.userId, token).catch((err) => {
         if (isMounted) {
            setIsConnected(false);
            setIsConnecting(false);
            // A rejected token is handled by sending the guest to sign-in, not with an error banner.
            if (!isUnauthorizedError(err)) showErrorBanner('Failed to connect to chat server.');
         }
      });

      return () => {
         isMounted = false;
         unsubscribe();
         setIsConnected(false);
         setIsConnecting(false);
         removeReceiveMessageListener();
         removeReceiveDraftListener();
         stopConnection();
      };
   }, [token, decodeJwt, handleReceiveMessage, handleReceiveDraft, recover]);

   // Tab regains visibility after a disconnect: restart the hub if it gave up,
   // and recover whatever was missed in the meantime.
   useEffect(() => {
      const handleVisibilityChange = () => {
         if (document.visibilityState !== 'visible') return;
         const up = isConnectionUp();
         if (!up) ensureConnected();
         if (!up || missedMessagesRef.current) recover();
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
   }, [recover]);

   return { isConnected, isConnecting };
};
